import React, { FC } from 'react';
import { Box, Typography } from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { formatDuration, getMeasurementUnit } from '../../../helpers/general.helpers';
import { palette } from '../../../theme';
import { WORKOUT_ZONE_DISPLAY_CONFIG } from './workout-zone.constants';

export interface WorkoutZoneBreakdownProps {
  durationComplete: number[];
  distanceComplete: number[];
}

const sectionHeaderSx = {
  textTransform: 'uppercase' as const,
  letterSpacing: '0.1em',
  fontSize: 11,
  fontWeight: 600,
  color: palette.text.secondary,
};

const WorkoutZoneBreakdown: FC<WorkoutZoneBreakdownProps> = ({ durationComplete, distanceComplete }) => {
  const safeDuration = durationComplete?.length === 6 ? durationComplete : [0, 0, 0, 0, 0, 0];
  const safeDistance = distanceComplete?.length === 6 ? distanceComplete : [0, 0, 0, 0, 0, 0];

  const totalDuration = safeDuration.reduce((acc, val) => acc + (val || 0), 0);
  const totalDistance = safeDistance.reduce((acc, val) => acc + (val || 0), 0);
  const measurementUnit = getMeasurementUnit();

  return (
    <Box>
      <Typography variant="caption" sx={{ ...sectionHeaderSx, display: 'block', mb: 0.75 }}>
        Heart Rate Zones
      </Typography>

      <Typography variant="body2" sx={{ fontWeight: 600, color: palette.primary.main, mb: 1.5, px: 1 }}>
        Total · {formatDuration(totalDuration)} hr · {totalDistance.toFixed(1)} {measurementUnit}
      </Typography>

      {WORKOUT_ZONE_DISPLAY_CONFIG.map(({ label, zoneIdx, color }) => {
        const barFill = totalDuration > 0 ? (safeDuration[zoneIdx] / totalDuration) * 100 : 0;

        return (
          <Box
            key={label}
            sx={{
              display: 'grid',
              gridTemplateColumns: '16px 52px 1fr 80px 64px',
              columnGap: 1,
              alignItems: 'center',
              py: 0.5,
              px: 1,
              borderRadius: 1.5,
            }}
          >
            <FiberManualRecordIcon sx={{ fontSize: 12, color }} />
            <Typography variant="body2" sx={{ fontWeight: 600, color: palette.text.primary }}>
              {label}
            </Typography>
            <Box
              sx={{
                overflow: 'hidden',
                borderRadius: '3px',
                border: `.5px solid ${palette.grey[200]}`,
                height: 6,
                backgroundColor: palette.grey[100],
              }}
            >
              <Box
                sx={{
                  width: `${barFill}%`,
                  height: '100%',
                  backgroundColor: color,
                  borderRadius: '3px',
                  transition: 'width 0.2s ease',
                }}
              />
            </Box>
            <Typography variant="body2" sx={{ textAlign: 'right', color: palette.text.secondary }}>
              {formatDuration(safeDuration[zoneIdx] || 0)}
            </Typography>
            <Typography variant="body2" sx={{ textAlign: 'right', color: palette.text.secondary }}>
              {(safeDistance[zoneIdx] || 0).toFixed(1)} {measurementUnit}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

export default WorkoutZoneBreakdown;
